<template>
	<div :class="tooltipClasses" aria-label="map mode panel">
		<Tooltip v-if="collapsed" :title="$t('home-map.expandMapModes')" placement="right">
			<Button shape="circle" size="small" class="expand-menu-button" @click="toggleCollapsed">
				<template #icon>
					<DoubleRightOutlined v-if="directionLTR" />
					<DoubleLeftOutlined v-else />
				</template>
			</Button>
		</Tooltip>
		<div v-else class="flex-wrapper header">
			<TypographyTitle class="flex-grow" :level="5">{{ $t('home-map.modeTitle') }}</TypographyTitle>
			<Tooltip :title="$t('home-map.collapseMapModes')">
				<Button shape="circle" size="small" @click="toggleCollapsed">
					<template #icon>
						<DoubleLeftOutlined v-if="directionLTR" />
						<DoubleRightOutlined v-else />
					</template>
				</Button>
			</Tooltip>
		</div>
		<Menu v-model:selectedKeys="selectedMapMode" mode="inline" :inline-collapsed="collapsed">
			<!-- global.less: .map-modes-position .ant-menu-item -->
			<MenuItem
				v-for="mapMode in mapModes"
				:key="mapMode.key"
				:aria-label="$t('home-map.modes.' + mapMode.key)"
			>
				<template #icon>
					<component :is="mapMode.icon" v-if="mapMode.icon" />
				</template>
				{{ $t('home-map.modes.' + mapMode.key) }}
			</MenuItem>
		</Menu>
		
	</div>
</template>

<script lang="ts">
import { computed } from 'vue';

import { TypographyTitle, Menu, MenuItem, Button, Tooltip } from 'ant-design-vue';
import {
	DoubleRightOutlined, DoubleLeftOutlined,
} from '@ant-design/icons-vue';

import { useBaseStationsStore } from '@/stores/baseStations';
import { useSessionStore } from '@/stores/session';
import { useLocaleData } from '@/locales/useLocaleData';
import { useAvailableMapModes } from '@/components/top_level/navigationConsts/mapModes';
import { MAP_MODE } from '@/components/map/consts/mapFeatureConsts';
import type { MapMode } from '@/components/map/types/mapFeatureTypes';

const MAP_MODE_VALUES = new Set<string>(Object.values(MAP_MODE));

const isMapMode = (value: string): value is MapMode => MAP_MODE_VALUES.has(value);

export default {
	components: {
		TypographyTitle, Menu, MenuItem, Button, Tooltip,
		DoubleRightOutlined, DoubleLeftOutlined,
	},
	setup() {
		const mapModes = useAvailableMapModes();

		const baseStationsStore = useBaseStationsStore();
		const selectedMapMode = computed({
			get: (): MapMode[] => [baseStationsStore.mode],
			set: value => {
				const mode = value[0];
				if (mode != null && isMapMode(mode)) {
					baseStationsStore.setMode(mode);
				}
			},
		})
		const sessionStore = useSessionStore();
		const collapsed = computed(() => sessionStore.mapModeChooserCollapsed);
		const tooltipClasses = computed(() => {
			let styles = ['map-modes-base', 'map-modes-position'];
			if (sessionStore.mapModeChooserCollapsed) {
				styles.push('map-modes-position-collapsed');
			}
			return styles;
		});
		const { directionLTR } = useLocaleData();
		return {
			mapModes,
			selectedMapMode,
			collapsed,
			toggleCollapsed: sessionStore.toggleMapModeChooserCollapsed,
			tooltipClasses,
			directionLTR,
		};
	},
}
</script>

<style scoped>
.map-modes-base {
	position: absolute;
	background-color: white;
	z-index: 2;
	box-shadow: 0px 2px 9px #444;
	border-radius: 5px;
}

.map-modes-position {
	padding: 8px 16px;
	bottom: 40px;
	inset-inline-start: 16px;
}

.header {
	margin-top: 3px;
}

.expand-menu-button {
	margin: 8px;
}

.map-modes-position-collapsed {
	padding: 8px;
}
</style>

<style>
.ant-menu.ant-menu-inline-collapsed {
	width: 40px !important;
	border: none !important;
}
</style>
